import { Answer, Answers } from "@/data/onboarding/types";
import { calculateBmi, toNumber } from "@/utils";

export type OnboardingReducerState = {
  currentQuestionId: string;
  answers: Answers;
  height?: number;
  weight?: number;
  bmi?: number;
  freetextData: Record<string, string>;
  questionnaireCompleted: boolean;
  multiSelections: Record<string, string[]>;
  currentOnboardingFlow?: FlowType;
};

export type FlowType = "obesity" | "hypertension";

type OnboardingAction =
  | { type: "setStep"; stepId: string }
  | { type: "answerQuestion"; answer: Answer }
  | { type: "setPhysicalStats"; height: string; weight: string }
  | { type: "setFreetextData"; name: string; data: string }
  | { type: "completeQuestionnaire" }
  | { type: "multiSelectToggle"; toggledOption: string }
  | { type: "setCurrentFlow"; flow: FlowType }
  | { type: "reset" };

export const initialState: OnboardingReducerState = {
  currentQuestionId: "q1",
  answers: {},
  freetextData: {},
  questionnaireCompleted: false,
  multiSelections: {},
};

const reducer = (
  state: OnboardingReducerState = initialState,
  action: OnboardingAction
): OnboardingReducerState => {
  switch (action.type) {
    case "setStep":
      return {
        ...state,
        currentQuestionId: action.stepId,
      };
    case "answerQuestion": {
      const exstingHypertensionDiagnosisQuestionId = "q1";

      const updatedAnswers = {
        ...state.answers,
        [state.currentQuestionId]: action.answer,
      };

      const isObesity =
        updatedAnswers[exstingHypertensionDiagnosisQuestionId] === undefined;

      return {
        ...state,
        answers: updatedAnswers,
        currentOnboardingFlow: isObesity ? "obesity" : "hypertension",
      };
    }
    case "setPhysicalStats": {
      const height = toNumber(action.height);
      const weight = toNumber(action.weight);

      return {
        ...state,
        height,
        weight,
        bmi: calculateBmi(height, weight),
      };
    }
    case "setFreetextData":
      return {
        ...state,
        freetextData: { ...state.freetextData, [action.name]: action.data },
      };
    case "completeQuestionnaire":
      return { ...state, questionnaireCompleted: true };
    case "multiSelectToggle": {
      let currentOptions = state.multiSelections[state.currentQuestionId] || [];
      if (currentOptions.includes(action.toggledOption)) {
        currentOptions = currentOptions.filter(
          (o) => o !== action.toggledOption
        );
      } else {
        currentOptions = [...currentOptions, action.toggledOption];
      }

      return {
        ...state,
        multiSelections: {
          ...state.multiSelections,
          [state.currentQuestionId]: currentOptions,
        },
      };
    }
    case "setCurrentFlow": {
      return { ...state, currentOnboardingFlow: action.flow };
    }
    case "reset":
      return { ...initialState };
    default:
      throw new Error("Unhandled action type");
  }
};

export default reducer;
